import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  /** Föreslaget namn, oftast lektionens rubrik eller ämnet. */
  suggestedName: string;
  /** Kort sammanfattning av vad mallen innehåller, visas som hjälptext. */
  summary: string;
  saving: boolean;
  onSave: (name: string, description: string) => void;
  onCancel: () => void;
}

/**
 * "Spara som mall" behöver ett namn för att mallen ska gå att känna igen i
 * schemarastret. Tidigare sparades den tyst under lektionens rubrik, vilket
 * gav fyra mallar som alla hette "Matematik".
 */
export function SaveTemplateDialog({
  open,
  suggestedName,
  summary,
  saving,
  onSave,
  onCancel,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState(suggestedName);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setName(suggestedName);
      setDescription('');
    }
  }, [open, suggestedName]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  const trimmed = name.trim();

  return (
    <dialog ref={ref} className="modal confirm" onCancel={onCancel} onClose={onCancel}>
      <form
        className="modal__inner confirm__inner"
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) onSave(trimmed, description.trim());
        }}
      >
        <h2 className="confirm__title">Spara som mall</h2>
        <p className="confirm__body">{summary}</p>

        <div className="field">
          <label htmlFor="template-name">Namn på mallen</label>
          <input
            id="template-name"
            type="text"
            value={name}
            autoFocus
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="template-desc">Kort beskrivning</label>
          <input
            id="template-desc"
            type="text"
            value={description}
            maxLength={200}
            placeholder="Till exempel: läsa → begrepp → diskutera i par"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="row confirm__actions">
          <span className="grow" />
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Avbryt
          </button>
          <button type="submit" className="button button--primary" disabled={!trimmed || saving}>
            {saving ? 'Sparar…' : 'Spara mallen'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
